import { Controller } from "@hotwired/stimulus"

// Filter, search, sort and page the Pulse SKU table in the browser. Filters are
// either a status (losing / thin / healthy) or a flag on the row (ads_drag,
// no_cogs, refunds); the Needs attention cards set the same filters. Every SKU
// is already in the page, so none of this re-requests anything; paging only
// keeps a long catalogue from rendering as one endless table.
// Lives on an element that wraps both the cards and the table.
export default class extends Controller {
  static targets = ["row", "chip", "search", "body", "empty", "table", "pager", "summary", "pageLabel", "prevButton", "nextButton", "sizeButton"]
  static values = { pageSize: { type: Number, default: 25 } }

  // The shortest page the pager offers: with no more rows than this there is nothing to page, so it stays out of the way
  static SMALLEST_PAGE = 10

  connect() {
    this.filter = "all"
    this.query = ""
    this.sortKey = null
    this.sortDir = "desc"
    this.page = 0
    this.pageSize = this.pageSizeValue
    this.render()
  }

  setFilter(event) {
    this.filter = event.params.filter
    this.page = 0
    this.render()
    // From a Needs attention card: bring the table into view
    if (!event.currentTarget.classList.contains("pulse-chip")) {
      this.tableTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  search() {
    this.query = this.searchTarget.value.trim().toLowerCase()
    this.page = 0
    this.render()
  }

  clear() {
    this.filter = "all"
    this.query = ""
    this.searchTarget.value = ""
    this.page = 0
    this.render()
  }

  sort(event) {
    const key = event.params.key
    this.sortDir = this.sortKey === key && this.sortDir === "desc" ? "asc" : "desc"
    this.sortKey = key

    const factor = this.sortDir === "desc" ? -1 : 1
    const value = (row) => parseFloat(row.dataset[`sort${key[0].toUpperCase()}${key.slice(1)}`])
    const sorted = [...this.rowTargets].sort((a, b) => factor * (value(a) - value(b)))
    sorted.forEach((row) => this.bodyTarget.appendChild(row))

    this.element.querySelectorAll(".pulse-sort").forEach((button) => {
      const active = button.dataset.skuTableKeyParam === key
      button.classList.toggle("is-sorted", active)
      button.dataset.dir = active ? this.sortDir : ""
    })

    this.page = 0
    this.render()
  }

  prev() {
    if (this.page > 0) { this.page -= 1; this.render() }
  }

  next() {
    if ((this.page + 1) * this.pageSize < this.matching().length) { this.page += 1; this.render() }
  }

  setSize(event) {
    this.pageSize = event.params.size
    this.page = 0
    this.render()
  }

  matches(row) {
    const matchesFilter =
      this.filter === "all" ||
      row.dataset.status === this.filter ||
      row.dataset.flags.split(" ").includes(this.filter)
    const matchesQuery = !this.query || row.dataset.search.includes(this.query)
    return matchesFilter && matchesQuery
  }

  // Rows passing the filter and search, in the order they are showing (rowTargets follows the DOM, so after a sort too)
  matching() {
    return this.rowTargets.filter((row) => this.matches(row))
  }

  render() {
    const matches = this.matching()
    const pages = Math.max(Math.ceil(matches.length / this.pageSize), 1)
    this.page = Math.min(this.page, pages - 1)
    const start = this.page * this.pageSize
    const shown = new Set(matches.slice(start, start + this.pageSize))
    this.rowTargets.forEach((row) => { row.hidden = !shown.has(row) })

    this.chipTargets.forEach((chip) => {
      chip.classList.toggle("is-active", chip.dataset.skuTableFilterParam === this.filter)
    })
    this.emptyTarget.classList.toggle("d-none", matches.length > 0 || this.rowTargets.length === 0)
    this.renderPager(matches.length, start, pages)
  }

  renderPager(total, start, pages) {
    if (!this.hasPagerTarget) return

    this.pagerTarget.classList.toggle("d-none", total <= this.constructor.SMALLEST_PAGE)
    this.summaryTarget.textContent = `${total ? start + 1 : 0} to ${Math.min(start + this.pageSize, total)} of ${total}`
    this.pageLabelTarget.textContent = `Page ${this.page + 1} of ${pages}`
    this.prevButtonTarget.disabled = this.page === 0
    this.nextButtonTarget.disabled = this.page >= pages - 1
    this.sizeButtonTargets.forEach((button) => {
      const active = button.dataset.skuTableSizeParam === String(this.pageSize)
      button.classList.toggle("btn-primary", active)
      button.classList.toggle("btn-outline-secondary", !active)
    })
  }
}
